/**
 * LocalDocumentState - Core document state management
 * 
 * Provides the foundational state container for the Client-First architecture,
 * ensuring immediate operations and consistent state management.
 */

import { 
  LocalDocumentState, 
  SelectionRange, 
  TextRange, 
  AttorneyInfo, 
  LegalDocumentType, 
  Jurisdiction 
} from '../types/DocumentTypes';

export class DocumentState {
  private state: LocalDocumentState;
  private eventListeners: Map<string, Function[]> = new Map();

  constructor(initialState?: Partial<LocalDocumentState>) {
    this.state = {
      // Core document data
      text: initialState?.text || '',
      caretPosition: initialState?.caretPosition || 0,
      selection: initialState?.selection,
      
      // Version tracking
      version: initialState?.version || 1,
      lastServerSync: initialState?.lastServerSync || 0,
      serverVersion: initialState?.serverVersion || 0,
      
      // Legal document metadata
      attorney: initialState?.attorney || this.getDefaultAttorney(),
      documentType: initialState?.documentType || 'contract',
      jurisdiction: initialState?.jurisdiction || this.getDefaultJurisdiction(),
      
      // Performance optimization
      isDirty: false,
      dirtyRegions: new Set<TextRange>(),
      
      // Document metadata
      created: initialState?.created || new Date(),
      modified: new Date(),
      title: initialState?.title,
      matterNumber: initialState?.matterNumber,
      clientName: initialState?.clientName
    };
  }

  // ============================================================================
  // STATE ACCESS
  // ============================================================================

  getText(): string {
    return this.state.text;
  }

  getCaretPosition(): number {
    return this.state.caretPosition;
  }

  getSelection(): SelectionRange | undefined {
    return this.state.selection || undefined;
  }

  getVersion(): number {
    return this.state.version;
  }

  getCompleteState(): LocalDocumentState {
    return { ...this.state };
  }

  // ============================================================================
  // STATE MUTATIONS
  // ============================================================================

  setText(text: string): void {
    if (text !== this.state.text) {
      this.state.text = text;
      this.state.version++;
      this.state.modified = new Date();
      this.state.isDirty = true;
      this.emit('textChanged', { text, version: this.state.version });
    }
  }

  setCaretPosition(position: number): void {
    const safePosition = Math.max(0, Math.min(position, this.state.text.length));
    if (safePosition !== this.state.caretPosition) {
      this.state.caretPosition = safePosition;
      this.emit('caretMoved', { position: safePosition });
    }
  }

  setSelection(selection: SelectionRange | undefined): void {
    this.state.selection = selection;
    this.emit('selectionChanged', { selection });
  }

  markDirty(region?: TextRange): void {
    this.state.isDirty = true;
    if (region) {
      this.state.dirtyRegions.add(region);
    }
    this.emit('documentDirty', { region });
  }

  clearDirtyRegions(): void {
    this.state.dirtyRegions.clear();
    this.state.isDirty = false;
    this.emit('documentClean');
  }

  // ============================================================================
  // TEXT OPERATIONS
  // ============================================================================

  insertText(position: number, text: string): TextRange {
    const safePosition = Math.max(0, Math.min(position, this.state.text.length));
    
    // Update text
    this.state.text = this.state.text.slice(0, safePosition) + 
                     text + 
                     this.state.text.slice(safePosition);
    
    // Update caret
    this.state.caretPosition = safePosition + text.length;
    
    // Mark dirty region
    const dirtyRegion: TextRange = {
      start: safePosition,
      end: safePosition + text.length
    };
    this.markDirty(dirtyRegion);
    
    // Update version and timestamp
    this.state.version++;
    this.state.modified = new Date();
    
    this.emit('textInserted', { position: safePosition, text, dirtyRegion });
    
    return dirtyRegion;
  }

  deleteRange(start: number, end: number): { deletedText: string; dirtyRegion: TextRange } {
    const safeStart = Math.max(0, start);
    const safeEnd = Math.min(end, this.state.text.length);
    
    if (safeStart >= safeEnd) {
      return { deletedText: '', dirtyRegion: { start: safeStart, end: safeStart } };
    }
    
    const deletedText = this.state.text.slice(safeStart, safeEnd);
    
    // Update text
    this.state.text = this.state.text.slice(0, safeStart) + 
                     this.state.text.slice(safeEnd);
    
    // Update caret
    this.state.caretPosition = safeStart;
    
    // Mark dirty region
    const dirtyRegion: TextRange = {
      start: safeStart,
      end: safeStart // After deletion, end equals start
    };
    this.markDirty(dirtyRegion);
    
    // Update version and timestamp
    this.state.version++;
    this.state.modified = new Date();
    
    this.emit('textDeleted', { start: safeStart, end: safeEnd, deletedText, dirtyRegion });
    
    return { deletedText, dirtyRegion };
  }

  // ============================================================================
  // LEGAL DOCUMENT OPERATIONS
  // ============================================================================

  insertParagraphBreak(position: number): TextRange {
    return this.insertText(position, '\n');
  }

  getTextAroundPosition(position: number, radius: number): string {
    const start = Math.max(0, position - radius);
    const end = Math.min(this.state.text.length, position + radius);
    return this.state.text.slice(start, end);
  }

  findText(searchText: string, startPosition: number = 0): number[] {
    const positions: number[] = [];
    let searchIndex = startPosition;
    
    while (searchIndex < this.state.text.length) {
      const foundIndex = this.state.text.indexOf(searchText, searchIndex);
      if (foundIndex === -1) break;
      
      positions.push(foundIndex);
      searchIndex = foundIndex + 1;
    }
    
    return positions;
  }

  // ============================================================================
  // LEGAL METADATA MANAGEMENT
  // ============================================================================

  updateAttorney(attorney: AttorneyInfo): void {
    this.state.attorney = attorney;
    this.emit('attorneyChanged', { attorney });
  }

  updateDocumentType(documentType: LegalDocumentType): void {
    this.state.documentType = documentType;
    this.emit('documentTypeChanged', { documentType });
  }

  updateJurisdiction(jurisdiction: Jurisdiction): void {
    this.state.jurisdiction = jurisdiction;
    this.emit('jurisdictionChanged', { jurisdiction });
  }

  updateMatterInfo(matterNumber: string, clientName: string): void {
    this.state.matterNumber = matterNumber;
    this.state.clientName = clientName;
    this.emit('matterInfoChanged', { matterNumber, clientName });
  }

  // ============================================================================
  // VERSION MANAGEMENT
  // ============================================================================

  incrementVersion(): number {
    this.state.version++;
    this.state.modified = new Date();
    this.emit('versionChanged', { version: this.state.version });
    return this.state.version;
  }

  updateServerSync(serverVersion: number): void {
    this.state.serverVersion = serverVersion;
    this.state.lastServerSync = Date.now();
    this.state.isDirty = false;
    this.clearDirtyRegions();
    this.emit('serverSynced', { serverVersion });
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      for (const listener of listeners) {
        try {
          listener(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      }
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private getDefaultAttorney(): AttorneyInfo {
    return {
      id: 'default-attorney',
      name: 'Default Attorney',
      email: 'attorney@example.com',
      barNumber: '000000',
      firmName: 'Default Firm',
      role: 'associate',
      seniority: 1
    };
  }

  private   getDefaultJurisdiction(): Jurisdiction {
    return 'federal' as Jurisdiction;
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Validate caret position
    if (this.state.caretPosition < 0 || this.state.caretPosition > this.state.text.length) {
      errors.push(`Invalid caret position: ${this.state.caretPosition}`);
    }
    
    // Validate selection
    if (this.state.selection) {
      const { start, end } = this.state.selection;
      if (start < 0 || end > this.state.text.length || start > end) {
        errors.push(`Invalid selection range: ${start}-${end}`);
      }
    }
    
    // Validate version consistency
    if (this.state.version < this.state.serverVersion) {
      errors.push(`Local version ${this.state.version} behind server version ${this.state.serverVersion}`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============================================================================
  // SERIALIZATION
  // ============================================================================

  serialize(): string {
    return JSON.stringify({
      ...this.state,
      dirtyRegions: Array.from(this.state.dirtyRegions)
    });
  }

  static deserialize(serialized: string): DocumentState {
    const data = JSON.parse(serialized);
    const state = new DocumentState(data);
    
    // Restore dirty regions
    if (data.dirtyRegions) {
      state.state.dirtyRegions = new Set(data.dirtyRegions);
    }
    
    return state;
  }

  // ============================================================================
  // MISSING METHODS FOR DMS INTEGRATION
  // ============================================================================

  /**
   * Set document title
   */
  setTitle(title: string): void {
    this.state.title = title;
    this.markDirty();
    this.emit('titleChanged', { title });
  }

  /**
   * Set matter number
   */
  setMatterNumber(matterNumber: string): void {
    this.state.matterNumber = matterNumber;
    this.markDirty();
    this.emit('matterNumberChanged', { matterNumber });
  }

  /**
   * Set client name
   */
  setClientName(clientName: string): void {
    this.state.clientName = clientName;
    this.markDirty();
    this.emit('clientNameChanged', { clientName });
  }

  /**
   * Mark document as clean (saved)
   */
  markClean(): void {
    this.state.isDirty = false;
    this.state.dirtyRegions.clear();
    this.emit('documentClean');
  }


}
